/* VastraGharr Luxe — product page enhancements
 * - <vg-recent-record>      records the viewed product handle in localStorage 'vg:recent'
 * - <vg-sticky-atc>         sticky add to cart bar (mirrors the main product form button) + compact size row;
 *                           sold out = "Notify me" (opens the buy buttons' WhatsApp message)
 * - <vg-delivery-estimate>  dispatch countdown + delivery days or dates in India time (Theme settings > Delivery)
 * - <vg-pincode>            6-digit pincode check -> estimated delivery window + COD line (localStorage 'vg:pincode')
 * - shopper-facing words    read from data attributes printed by Liquid (data-label / data-notify-label on the sticky
 *                           bar, data-vg-word-* on <product-info>), today's wording as the fallback
 * - <vg-whatsapp-order>     WhatsApp order link that follows the selected variant
 * - <vg-gallery-dots>       mobile gallery dots (+ keeps .is-active on the visible slide for fly-to-bag)
 * - <vg-size-guide>         size guide drawer (<dialog>) with inch/cm toggle
 * - variant sync            [data-vg-sync] fragments refreshed from Dawn's variantChange section HTML;
 *                           sold-out "Notify me on WhatsApp" panel, Shiprocket button inert while unavailable,
 *                           share links on the selected variant
 * - add to cart             .is-added for 1.6 s on the button that added (contract 6)
 * - accordions              smooth height animation for details[data-vg-animate]
 * - gallery                 desktop counter, vertical thumbnail rail arrows + centring
 * - VG.lightbox             media lightbox: swipe strip on phones, pinch / double-tap zoom + pan, counter
 * - Swatchfy bridge         size guide link in the app's label slot, low-stock dot on its size pills
 * - related products        loaded on idle while far below the fold (no footer jump on fast scrolls)
 */
(function () {
  'use strict';

  if (window.VG && window.VG.productScriptLoaded) return;
  window.VG = window.VG || {};
  window.VG.productScriptLoaded = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileMedia = window.matchMedia('(max-width: 749px)');
  var railMedia = window.matchMedia('(min-width: 990px)');
  var RECENT_KEY = 'vg:recent';
  var PINCODE_KEY = 'vg:pincode';
  var RECENT_MAX = 12;
  var ADDED_MS = 1600;
  var DAY = 86400000;
  var IST_OFFSET = 330 * 60 * 1000; /* India Standard Time is UTC+05:30 all year */
  var EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

  /* constants.js declares PUB_SUB_EVENTS with `const`: a global binding, but not a window property. */
  function hasPubSub() {
    return typeof window.subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(String(value));
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* storage unavailable (private mode / blocked) */
    }
  }

  /* Fill an element with text where a [token] is replaced by a <strong> value. */
  function fillTemplate(el, template, token, value) {
    el.textContent = '';
    var parts = String(template || '').split(token);
    if (parts.length === 1) {
      el.appendChild(document.createTextNode(parts[0] ? parts[0] + ' ' : ''));
      var s = document.createElement('strong');
      s.textContent = value;
      el.appendChild(s);
      return;
    }
    parts.forEach(function (part, index) {
      if (part) el.appendChild(document.createTextNode(part));
      if (index < parts.length - 1) {
        var strong = document.createElement('strong');
        strong.textContent = value;
        el.appendChild(strong);
      }
    });
  }

  /* Shopper-facing words from the theme editor: Liquid prints data-vg-word-<name> on the element or an ancestor
     (sections/main-product.liquid puts them on <product-info> from the block settings). Today's wording is the fallback. */
  function word(el, name, fallback) {
    var attr = 'data-vg-word-' + name;
    var holder = el && el.closest ? el.closest('[' + attr + ']') : null;
    var value = holder ? holder.getAttribute(attr) : '';
    return value || fallback;
  }

  /* Replace [token] in a word template; without the token the value is added after it. */
  function fillWord(template, token, value) {
    var text = String(template || '');
    return text.indexOf(token) === -1 ? (text ? text + ' ' : '') + value : text.split(token).join(value);
  }

  /* ---------------------------------------------------------------- Delivery window (India time) */
  function isSunday(ms) {
    return new Date(ms).getUTCDay() === 0;
  }

  /* calendar: count every day (Theme settings > Delivery > Count: Days); otherwise Sundays are skipped. */
  function addWorkingDays(start, count, calendar) {
    if (calendar) return start + Math.max(count, 0) * DAY;
    var date = start;
    var added = 0;
    while (added < count) {
      date += DAY;
      if (!isSunday(date)) added += 1;
    }
    return date;
  }

  function formatDay(ms, options) {
    try {
      return new Date(ms).toLocaleDateString('en-IN', Object.assign({ timeZone: 'UTC' }, options));
    } catch (e) {
      return new Date(ms).toDateString();
    }
  }

  /* Dispatch today before the cut-off (not on Sundays), else the next working day; delivery min..max days later
     (working days unless calendar is true). */
  function deliveryWindow(minDays, maxDays, cutoffHour, calendar) {
    var cutoff = isNaN(cutoffHour) ? 14 : Math.min(Math.max(cutoffHour, 0), 24);
    var min = Math.max(minDays || 0, 0);
    var max = Math.max(maxDays || min, min);
    var now = new Date(Date.now() + IST_OFFSET); /* UTC fields now read as India wall-clock time */
    var today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    var secondsNow = now.getUTCHours() * 3600 + now.getUTCMinutes() * 60 + now.getUTCSeconds();
    var cutoffSeconds = Math.round(cutoff * 3600);
    var dispatch = today;
    var dispatchToday = !isSunday(today) && secondsNow < cutoffSeconds;
    if (!dispatchToday) {
      dispatch = today + DAY;
      while (isSunday(dispatch)) dispatch += DAY;
    }
    return {
      today: today,
      dispatch: dispatch,
      dispatchToday: dispatchToday,
      secondsLeft: cutoffSeconds - secondsNow,
      from: addWorkingDays(dispatch, min, calendar),
      to: addWorkingDays(dispatch, max, calendar),
    };
  }

  function formatRange(from, to) {
    var options = { weekday: 'short', day: 'numeric', month: 'short' };
    return from === to ? formatDay(from, options) : formatDay(from, options) + ' – ' + formatDay(to, options);
  }

  window.VG.deliveryWindow = deliveryWindow;

  /* ---------------------------------------------------------------- Recently viewed */
  window.VG.recent = window.VG.recent || {
    key: RECENT_KEY,
    get: function () {
      try {
        var list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
        return Array.isArray(list) ? list.filter(function (h) { return typeof h === 'string' && h; }) : [];
      } catch (e) {
        return [];
      }
    },
    add: function (handle) {
      if (!handle) return;
      try {
        var list = [handle].concat(this.get().filter(function (h) { return h !== handle; })).slice(0, RECENT_MAX);
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
        document.dispatchEvent(new CustomEvent('vg:recent:change', { detail: { handles: list } }));
      } catch (e) {
        /* storage unavailable (private mode / blocked) — nothing to record */
      }
    },
  };

  if (!customElements.get('vg-recent-record')) {
    customElements.define(
      'vg-recent-record',
      class extends HTMLElement {
        connectedCallback() {
          if (window.Shopify && window.Shopify.designMode) return;
          window.VG.recent.add(this.dataset.handle);
        }
      }
    );
  }

  /* ---------------------------------------------------------------- Buy buttons state */
  function setShiprocketWrap(wrap, enabled) {
    wrap.classList.toggle('is-disabled', !enabled);
    /* inert: the disabled Fastrr button must not be reachable with Tab (QA edge case) */
    if (enabled) {
      wrap.removeAttribute('inert');
      wrap.removeAttribute('aria-hidden');
    } else {
      wrap.setAttribute('inert', '');
      wrap.setAttribute('aria-hidden', 'true');
    }
  }

  function notifyMessage(notify, variant) {
    var message = (notify.dataset.intro || '') + ' ' + (notify.dataset.title || '');
    if (notify.dataset.defaultVariant !== 'true' && variant && variant.title) message += ' (' + variant.title + ')';
    message += ' — ' + notify.dataset.url + (variant && variant.id ? '?variant=' + variant.id : '');
    return 'https://wa.me/' + notify.dataset.number + '?text=' + encodeURIComponent(message.trim());
  }

  /* Sold out (variant exists, not available) -> "Notify me on WhatsApp" replaces the add to cart row.
     Unavailable combination (no variant) -> Dawn's disabled "Unavailable" button stays. */
  function updateBuyState(info, variant) {
    if (!info) return;
    var available = Boolean(variant && variant.available);
    var soldOut = Boolean(variant && !variant.available);
    info.querySelectorAll('[data-vg-shiprocket]').forEach(function (wrap) {
      setShiprocketWrap(wrap, available);
    });
    info.querySelectorAll('product-form[data-vg-buy]').forEach(function (form) {
      var notify = form.querySelector('[data-vg-notify]');
      if (!notify) return;
      form.classList.toggle('is-soldout', soldOut);
      notify.hidden = !soldOut;
      if (!soldOut) return;
      var label = notify.querySelector('[data-vg-notify-variant]');
      if (label) label.textContent = variant.title || '';
      var link = notify.querySelector('[data-vg-notify-link]');
      if (link) link.href = notifyMessage(notify, variant);
    });
  }

  function updateShare(info, variant) {
    if (!info || !variant || !variant.id) return;
    info.querySelectorAll('[data-vg-share]').forEach(function (share) {
      var url = share.dataset.url + '?variant=' + variant.id;
      var wa = share.querySelector('[data-vg-share-whatsapp]');
      if (wa) wa.href = 'https://wa.me/?text=' + encodeURIComponent(((share.dataset.title || '') + ' ' + url).trim());
      var copy = share.querySelector('[data-vg-share-copy]');
      if (copy) copy.setAttribute('data-vg-copy', url);
    });
  }

  /* The product-info elements a variantChange belongs to: Dawn has already written the new id into the form. */
  function infosForVariant(productId, variant) {
    var infos = Array.prototype.slice.call(
      document.querySelectorAll('product-info[data-product-id="' + cssEscape(productId) + '"]')
    );
    if (infos.length < 2 || !variant) return infos;
    var matches = infos.filter(function (info) {
      var input = info.querySelector('product-form input[name="id"]');
      return input && String(input.value) === String(variant.id);
    });
    return matches.length ? matches : infos;
  }

  /* ---------------------------------------------------------------- Variant sync */
  function productIdFromHtml(html) {
    var info = html && html.querySelector ? html.querySelector('product-info[data-product-id]') : null;
    return info ? info.dataset.productId : null;
  }

  function onVariantChange(payload) {
    var data = payload && payload.data;
    if (!data || !data.html) return;
    var productId = productIdFromHtml(data.html);
    if (!productId) return;
    var variant = data.variant || null;
    var pid = cssEscape(productId);

    document.querySelectorAll('[data-vg-sync][data-vg-product="' + pid + '"]').forEach(function (el) {
      var source = data.html.querySelector('[data-vg-sync="' + cssEscape(el.dataset.vgSync) + '"]');
      if (!source) return;
      el.innerHTML = source.innerHTML;
      el.hidden = source.hasAttribute('hidden');
    });

    /* Custom add to cart label (e.g. "Pre-order") — Dawn resets it to "Add to cart" on variant change. */
    document
      .querySelectorAll('product-info[data-product-id="' + pid + '"] .vg-atc[data-vg-label]')
      .forEach(function (button) {
        if (button.hasAttribute('disabled')) return;
        var label = button.querySelector('span');
        if (label) label.textContent = button.dataset.vgLabel;
      });

    infosForVariant(productId, variant).forEach(function (info) {
      updateBuyState(info, variant);
      updateShare(info, variant);
    });

    document.dispatchEvent(
      new CustomEvent('vg:variant', {
        detail: { productId: productId, variant: variant, html: data.html, sectionId: data.sectionId },
      })
    );
  }

  /* Variant table from buy-buttons (script[data-vg-variants]), keyed by Dawn's option value ids. */
  function variantTable(info) {
    var script = info.querySelector('script[data-vg-variants][data-product-id="' + cssEscape(info.dataset.productId) + '"]');
    if (!script) return null;
    if (script.vgTable !== undefined) return script.vgTable;
    var table = null;
    try {
      var data = JSON.parse(script.textContent);
      if (data && Array.isArray(data.options) && Array.isArray(data.variants)) {
        table = {};
        data.variants.forEach(function (row) {
          var names = row[2] || [];
          if (names.length !== data.options.length) return;
          var ids = names.map(function (name, index) {
            var id = data.options[index] ? data.options[index][name] : null;
            return id === null || id === undefined ? '' : String(id);
          });
          if (ids.indexOf('') !== -1) return;
          table[ids.join(',')] = { id: row[0], available: row[1] === true, title: row[3] || '' };
        });
      }
    } catch (error) {
      table = null;
    }
    script.vgTable = table;
    return table;
  }

  /* The variant Dawn is about to fetch for this selection, or null when it cannot be told here
     (a product swap, a combination that does not exist, or no table). */
  function pendingVariant(info, target, selectedOptionValues) {
    if (!Array.isArray(selectedOptionValues) || !selectedOptionValues.length) return null;
    if (target && target.dataset && target.dataset.productUrl && target.dataset.productUrl !== info.dataset.url) return null;
    var table = variantTable(info);
    if (!table) return null;
    var key = selectedOptionValues.map(function (id) {
      return id === null || id === undefined ? '' : String(id);
    });
    if (key.indexOf('') !== -1) return null;
    return table[key.join(',')] || null;
  }

  function onOptionChange(payload) {
    var data = (payload && payload.data) || {};
    var target = data.target;
    var info = target && target.closest ? target.closest('product-info[data-product-id]') : null;
    if (!info) return;
    /* Dawn writes the new id into the form only after its section fetch. Write it now, like the live theme, so the
       Shiprocket Buy now can stay tappable during the fetch and still sends the size on screen. When the variant
       cannot be told here, the button waits for Dawn's variantChange as before (no wrong-size checkout). */
    var variant = pendingVariant(info, target, data.selectedOptionValues);
    if (variant && info.dataset.section) {
      var section = cssEscape(info.dataset.section);
      info
        .querySelectorAll('#product-form-' + section + ' input[name="id"], #product-form-installment-' + section + ' input[name="id"]')
        .forEach(function (input) {
          input.value = variant.id;
        });
    }
    if (variant) {
      /* Buy now state and the sold-out "Notify me" row follow at once too (variantChange repeats it). */
      updateBuyState(info, variant);
    } else {
      info.querySelectorAll('[data-vg-shiprocket]').forEach(function (wrap) {
        setShiprocketWrap(wrap, false);
      });
    }
    /* Dawn publishes no variantChange for an unavailable combination, so blocks that echo the
       selection (WhatsApp order, sticky size row) listen to this instead of waiting for vg:variant. */
    document.dispatchEvent(
      new CustomEvent('vg:option', { detail: { productId: info.dataset.productId, productInfo: info } })
    );
  }

  /* ---------------------------------------------------------------- Add to cart: .is-added (contract 6) */
  var pendingAdd = null;

  function markAdded(button) {
    if (!button) return;
    window.clearTimeout(button.vgAddedTimer);
    button.classList.add('is-added');
    button.vgAddedTimer = window.setTimeout(function () {
      button.classList.remove('is-added');
    }, ADDED_MS);
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      var productForm = form && form.closest ? form.closest('product-form') : null;
      if (!productForm) return;
      var idInput = form.querySelector('[name="id"]');
      pendingAdd = {
        button: productForm.querySelector('[type="submit"][name="add"]'),
        variantId: idInput ? String(idInput.value) : '',
        at: Date.now(),
      };
    },
    true
  );

  function onCartUpdate(event) {
    if (!event || event.source !== 'product-form' || !pendingAdd) return;
    if (String(event.productVariantId) !== pendingAdd.variantId || Date.now() - pendingAdd.at > 30000) return;
    var button = pendingAdd.button;
    pendingAdd = null;
    if (!button || !button.isConnected) return;
    markAdded(button);
    document.querySelectorAll('vg-sticky-atc').forEach(function (bar) {
      if (typeof bar.markAdded === 'function' && bar.mainButton === button) bar.markAdded();
    });
  }

  if (hasPubSub()) {
    window.subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
    window.subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, onOptionChange);
    window.subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
  }

  /* A combination that does not exist ends in Dawn's setUnavailable() without a variantChange, so the
     stock line and delivery card would keep describing the previous variant. Hide them there. */
  function onUnavailable(info) {
    var productId = info && info.dataset ? info.dataset.productId : null;
    if (!productId) return;
    document
      .querySelectorAll('[data-vg-sync="stock"][data-vg-product="' + cssEscape(productId) + '"]')
      .forEach(function (el) {
        el.hidden = true;
      });
    updateBuyState(info, null);
    document.dispatchEvent(new CustomEvent('vg:variant', { detail: { productId: productId, variant: null } }));
  }

  if (window.customElements && typeof customElements.whenDefined === 'function') {
    customElements.whenDefined('product-info').then(function () {
      var ProductInfo = customElements.get('product-info');
      var proto = ProductInfo && ProductInfo.prototype;
      if (!proto || typeof proto.setUnavailable !== 'function' || proto.vgUnavailableHooked) return;
      var setUnavailable = proto.setUnavailable;
      proto.setUnavailable = function () {
        var result = setUnavailable.apply(this, arguments);
        onUnavailable(this);
        return result;
      };
      proto.vgUnavailableHooked = true;
    });
  }

  /* ---------------------------------------------------------------- Sticky add to cart */
  if (!customElements.get('vg-sticky-atc')) {
    customElements.define(
      'vg-sticky-atc',
      class VgStickyAtc extends HTMLElement {
        connectedCallback() {
          if (this.vgReady || this.closest('quick-add-modal')) return;
          this.vgReady = true;
          this.sectionId = this.dataset.section;
          this.button = this.querySelector('[data-vg-sticky-submit]');
          this.labelEl = this.querySelector('[data-vg-sticky-label]');
          this.sizes = this.querySelector('[data-vg-sticky-sizes]');
          this.visible = false;
          this.onClick = this.onClick.bind(this);
          this.onVariant = this.onVariant.bind(this);
          this.onOption = this.onOption.bind(this);
          this.onSizeClick = this.onSizeClick.bind(this);
          this.onResize = this.updateOffset.bind(this);
          if (this.button) this.button.addEventListener('click', this.onClick);
          if (this.sizes) this.sizes.addEventListener('click', this.onSizeClick);
          document.addEventListener('vg:variant', this.onVariant);
          document.addEventListener('vg:option', this.onOption);
          window.addEventListener('resize', this.onResize, { passive: true });
          this.attach();
          this.syncSizes();
        }

        disconnectedCallback() {
          this.vgReady = false;
          this.detach();
          if (this.button) this.button.removeEventListener('click', this.onClick);
          if (this.sizes) this.sizes.removeEventListener('click', this.onSizeClick);
          document.removeEventListener('vg:variant', this.onVariant);
          document.removeEventListener('vg:option', this.onOption);
          window.removeEventListener('resize', this.onResize);
          window.clearTimeout(this.addedTimer);
          document.documentElement.classList.remove('vg-sticky-atc-visible');
          document.documentElement.style.setProperty('--vg-sticky-atc-h', '0px');
          document.documentElement.style.setProperty('--vg-float-offset', '0px');
        }

        get mainButton() {
          return document.getElementById('ProductSubmitButton-' + this.sectionId);
        }

        get productForm() {
          var button = this.mainButton;
          return button ? button.closest('product-form') : null;
        }

        get variantSelects() {
          return document.getElementById('variant-selects-' + this.sectionId);
        }

        attach() {
          var button = this.mainButton;
          if (!button || button === this.observed) return;
          this.detach();
          this.observed = button;
          var target = button.closest('.product-form__buttons') || button;
          var form = button.closest('product-form');

          /* product-form wraps the add to cart row and the sold-out "Notify me" panel, whichever is showing */
          this.intersection = new IntersectionObserver(
            function (entries) {
              var entry = entries[0];
              this.toggle(!entry.isIntersecting && entry.boundingClientRect.top < 0);
            }.bind(this),
            { threshold: 0 }
          );
          this.intersection.observe(form || target);

          this.mutation = new MutationObserver(this.syncState.bind(this));
          this.mutation.observe(button, {
            attributes: true,
            attributeFilter: ['disabled', 'class', 'aria-disabled'],
            childList: true,
            subtree: true,
            characterData: true,
          });
          if (form) this.mutation.observe(form, { attributes: true, attributeFilter: ['class'] });
          this.syncState();
        }

        detach() {
          if (this.intersection) this.intersection.disconnect();
          if (this.mutation) this.mutation.disconnect();
          this.intersection = null;
          this.mutation = null;
          this.observed = null;
        }

        onVariant(event) {
          if (!event.detail || String(event.detail.productId) !== String(this.dataset.productId)) return;
          /* The product form may have been re-rendered (combined listings) */
          if (this.mainButton !== this.observed) this.attach();
          this.syncState();
          this.syncSizes();
        }

        onOption(event) {
          if (!event.detail || String(event.detail.productId) !== String(this.dataset.productId)) return;
          this.syncSizes();
        }

        toggle(show) {
          if (show === this.visible) return;
          this.visible = show;
          this.classList.toggle('is-visible', show);
          this.setAttribute('aria-hidden', String(!show));
          if (show) {
            this.removeAttribute('inert');
          } else {
            this.setAttribute('inert', '');
          }
          document.documentElement.classList.toggle('vg-sticky-atc-visible', show);
          this.updateOffset();
        }

        updateOffset() {
          var height = this.visible ? this.offsetHeight : 0;
          var root = document.documentElement.style;
          root.setProperty('--vg-sticky-atc-h', height + 'px');
          /* lifts the WhatsApp float and toasts above the bar */
          root.setProperty('--vg-float-offset', height + 'px');
        }

        notifyLink() {
          var form = this.productForm;
          if (!form || !form.classList.contains('is-soldout')) return null;
          return form.querySelector('[data-vg-notify]:not([hidden]) [data-vg-notify-link]');
        }

        syncState() {
          var button = this.mainButton;
          if (!button || !this.button) return;
          var disabled = button.hasAttribute('disabled');
          var notify = this.notifyLink();
          var mainLabel = button.querySelector('span');
          var text = mainLabel ? mainLabel.textContent.trim() : '';
          this.button.classList.toggle('is-unavailable', disabled && !notify);
          this.button.classList.toggle('is-notify', Boolean(notify));
          this.button.classList.toggle('loading', button.classList.contains('loading'));
          if (!this.labelEl) return;
          if (notify) {
            this.labelEl.textContent = this.dataset.notifyLabel || 'Notify me';
          } else {
            this.labelEl.textContent = disabled ? text || this.dataset.label : this.dataset.label;
          }
        }

        markAdded() {
          if (!this.button) return;
          var button = this.button;
          window.clearTimeout(this.addedTimer);
          button.classList.add('is-added');
          this.addedTimer = window.setTimeout(function () {
            button.classList.remove('is-added');
          }, ADDED_MS);
        }

        /* Main picker inputs for the sticky size row's option */
        sizeInputs() {
          var selects = this.variantSelects;
          if (!selects || !this.sizes) return [];
          var name = this.sizes.dataset.optionName + '-' + this.sizes.dataset.optionPosition;
          var radios = Array.prototype.filter.call(selects.querySelectorAll('input[type="radio"]'), function (input) {
            return input.name === name;
          });
          if (radios.length) return radios;
          var select = Array.prototype.find.call(selects.querySelectorAll('select'), function (el) {
            return el.name === 'options[' + this.sizes.dataset.optionName + ']';
          }, this);
          return select ? Array.prototype.slice.call(select.options) : [];
        }

        syncSizes() {
          if (!this.sizes) return;
          var inputs = this.sizeInputs();
          if (!inputs.length) return;
          this.sizes.querySelectorAll('[data-vg-sticky-size]').forEach(function (chip) {
            var value = chip.dataset.vgStickySize;
            var input = inputs.find(function (el) {
              return el.value === value;
            });
            if (!input) return;
            var selected = input.tagName === 'OPTION' ? input.selected : input.checked;
            var soldOut =
              input.classList.contains('disabled') ||
              input.classList.contains('visually-disabled') ||
              input.disabled === true;
            var label = input.tagName === 'INPUT' && input.id ? document.querySelector('label[for="' + cssEscape(input.id) + '"]') : null;
            chip.classList.toggle('is-selected', selected);
            chip.classList.toggle('is-soldout', soldOut);
            chip.classList.toggle('is-low', Boolean(label && label.classList.contains('vg-value--low')));
            chip.setAttribute('aria-pressed', String(selected));
          });
        }

        onSizeClick(event) {
          var chip = event.target.closest('[data-vg-sticky-size]');
          if (!chip || !this.sizes.contains(chip)) return;
          var value = chip.dataset.vgStickySize;
          var input = this.sizeInputs().find(function (el) {
            return el.value === value;
          });
          if (!input) return;
          this.sizes.querySelectorAll('[data-vg-sticky-size]').forEach(function (other) {
            other.classList.toggle('is-selected', other === chip);
            other.setAttribute('aria-pressed', String(other === chip));
          });
          if (input.tagName === 'OPTION') {
            var select = input.parentElement;
            if (select.value === value) return;
            select.value = value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          } else if (!input.checked) {
            /* a real click on the radio: Dawn's variant-selects handles the change, fetch and re-render */
            input.click();
          }
        }

        onClick() {
          var button = this.mainButton;
          if (!button) return;
          var notify = this.notifyLink();
          if (notify) {
            window.open(notify.href, '_blank', 'noopener');
            return;
          }
          if (button.hasAttribute('disabled')) {
            this.scrollToPicker();
            return;
          }
          if (button.getAttribute('aria-disabled') === 'true') return; /* add in progress */
          button.click();
        }

        scrollToPicker() {
          var picker =
            document.getElementById('variant-selects-' + this.sectionId) ||
            document.getElementById('ProductInfo-' + this.sectionId);
          if (!picker) return;
          var header = document.querySelector('.section-header');
          var offset = header ? header.getBoundingClientRect().height + 16 : 90;
          var top = picker.getBoundingClientRect().top + window.pageYOffset - offset;
          window.scrollTo({ top: Math.max(top, 0), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
          picker.classList.remove('vg-attention');
          void picker.offsetWidth;
          picker.classList.add('vg-attention');
          window.setTimeout(function () {
            picker.classList.remove('vg-attention');
          }, 1800);
          var firstInput = picker.querySelector('input:not(.disabled), select');
          if (firstInput) firstInput.focus({ preventScroll: true });
        }
      }
    );
  }

  /* ---------------------------------------------------------------- Delivery estimate */
  if (!customElements.get('vg-delivery-estimate')) {
    customElements.define(
      'vg-delivery-estimate',
      class VgDeliveryEstimate extends HTMLElement {
        connectedCallback() {
          if (this.vgReady) return;
          this.vgReady = true;
          this.cutoff = parseFloat(this.dataset.cutoff);
          this.minDays = Math.max(parseInt(this.dataset.min, 10) || 0, 0);
          this.maxDays = Math.max(parseInt(this.dataset.max, 10) || this.minDays, this.minDays);
          this.dispatchRow = this.querySelector('[data-vg-dispatch]');
          this.dispatchText = this.querySelector('[data-vg-dispatch-text]');
          this.deliveryText = this.querySelector('[data-vg-delivery-text]');
          this.domestic = this.querySelector('[data-vg-domestic]');
          this.international = this.querySelector('[data-vg-international]');
          this.foot = this.querySelector('[data-vg-delivery-foot]');
          this.onVariant = this.onVariant.bind(this);
          document.addEventListener('vg:variant', this.onVariant);
          this.setAvailable(this.dataset.available !== 'false');
          this.render();
          this.timer = window.setInterval(this.render.bind(this), 30000);
        }

        disconnectedCallback() {
          this.vgReady = false;
          window.clearInterval(this.timer);
          document.removeEventListener('vg:variant', this.onVariant);
        }

        onVariant(event) {
          if (!event.detail || String(event.detail.productId) !== String(this.dataset.productId)) return;
          var info = this.closest('product-info');
          if (info && event.detail.variant) {
            var input = info.querySelector('product-form input[name="id"]');
            if (input && input.value && String(input.value) !== String(event.detail.variant.id)) return;
          }
          this.setAvailable(Boolean(event.detail.variant && event.detail.variant.available));
          this.render();
        }

        setAvailable(available) {
          this.classList.toggle('is-unavailable', !available);
          this.hidden = !available;
        }

        isInternational() {
          var country = (window.Shopify && window.Shopify.country) || this.dataset.country || 'IN';
          return String(country).toUpperCase() !== 'IN';
        }

        render() {
          var international = this.isInternational();
          if (this.domestic) this.domestic.hidden = international;
          if (this.international) this.international.hidden = !international;
          if (this.foot) this.foot.hidden = international;
          if (international) return;

          var win = deliveryWindow(this.minDays, this.maxDays, this.cutoff, this.dataset.unit !== 'working');

          if (this.dispatchRow && this.dispatchText) {
            if (this.dataset.countdown === 'false') {
              this.dispatchRow.hidden = true;
            } else {
              if (win.dispatchToday) {
                var left = win.secondsLeft;
                var hours = Math.floor(left / 3600);
                var minutes = Math.ceil((left % 3600) / 60);
                if (hours === 0) minutes = Math.max(minutes, 1);
                if (minutes === 60) {
                  hours += 1;
                  minutes = 0;
                }
                var timeLabel = hours > 0 ? hours + 'h ' + minutes + 'm' : minutes + 'm';
                fillTemplate(this.dispatchText, this.dataset.countdownText, '[time]', timeLabel);
              } else {
                var dayLabel =
                  win.dispatch === win.today + DAY
                    ? word(this, 'tomorrow', 'tomorrow')
                    : fillWord(word(this, 'weekday', 'on [weekday]'), '[weekday]', formatDay(win.dispatch, { weekday: 'long' }));
                fillTemplate(this.dispatchText, this.dataset.laterText, '[day]', dayLabel);
              }
              this.dispatchRow.hidden = false;
            }
          }

          // "Number of days" is printed by Liquid (snippets/vg-delivery-estimate.liquid); only dates are filled in here.
          if (this.deliveryText && this.dataset.display === 'dates') {
            fillTemplate(this.deliveryText, this.dataset.deliveryText, '[range]', formatRange(win.from, win.to));
          }
        }
      }
    );
  }

  /* ---------------------------------------------------------------- Pincode check */
  if (!customElements.get('vg-pincode')) {
    customElements.define(
      'vg-pincode',
      class VgPincode extends HTMLElement {
        connectedCallback() {
          if (this.vgReady || this.closest('quick-add-modal')) return;
          this.vgReady = true;
          var country = (window.Shopify && window.Shopify.country) || this.dataset.country || 'IN';
          if (String(country).toUpperCase() !== 'IN') {
            this.hidden = true;
            return;
          }
          this.form = this.querySelector('[data-vg-pincode-form]');
          this.input = this.querySelector('[data-vg-pincode-input]');
          this.result = this.querySelector('[data-vg-pincode-result]');
          if (!this.form || !this.input || !this.result) return;
          this.prefixes = String(this.dataset.fastPrefixes || '')
            .split(',')
            .map(function (prefix) {
              return prefix.replace(/\D/g, '');
            })
            .filter(Boolean);

          this.onSubmit = this.onSubmit.bind(this);
          this.onInput = this.onInput.bind(this);
          this.onResultClick = this.onResultClick.bind(this);
          this.form.addEventListener('submit', this.onSubmit);
          this.input.addEventListener('input', this.onInput);
          this.result.addEventListener('click', this.onResultClick);

          var saved = storageGet(PINCODE_KEY);
          if (saved && /^[1-9][0-9]{5}$/.test(saved)) {
            this.input.value = saved;
            this.check(false);
          }
        }

        disconnectedCallback() {
          this.vgReady = false;
          if (!this.form) return;
          this.form.removeEventListener('submit', this.onSubmit);
          this.input.removeEventListener('input', this.onInput);
          this.result.removeEventListener('click', this.onResultClick);
        }

        onInput() {
          var digits = this.input.value.replace(/\D/g, '').slice(0, 6);
          if (digits !== this.input.value) this.input.value = digits;
          if (this.classList.contains('is-invalid')) {
            this.classList.remove('is-invalid');
            this.input.removeAttribute('aria-invalid');
          }
        }

        onSubmit(event) {
          event.preventDefault();
          this.check(true);
        }

        onResultClick(event) {
          if (!event.target.closest('[data-vg-pincode-change]')) return;
          event.preventDefault();
          this.classList.remove('is-checked');
          this.input.focus();
          this.input.select();
        }

        check(fromUser) {
          var pin = this.input.value.replace(/\D/g, '');
          this.result.textContent = '';
          if (!/^[1-9][0-9]{5}$/.test(pin)) {
            this.classList.add('is-invalid');
            this.classList.remove('is-checked');
            this.input.setAttribute('aria-invalid', 'true');
            var error = document.createElement('p');
            error.className = 'vg-pincode__error';
            error.textContent = word(this, 'pincode-error', 'Please enter a valid 6-digit pincode.');
            this.result.appendChild(error);
            if (fromUser) this.input.focus();
            return;
          }

          this.classList.remove('is-invalid');
          this.input.removeAttribute('aria-invalid');
          var fast = this.prefixes.some(function (prefix) {
            return pin.indexOf(prefix) === 0;
          });
          var min = parseInt(fast ? this.dataset.fastMin : this.dataset.min, 10);
          var max = parseInt(fast ? this.dataset.fastMax : this.dataset.max, 10);
          var win = deliveryWindow(min, max, parseFloat(this.dataset.cutoff), this.dataset.unit !== 'working');

          var line = document.createElement('p');
          line.className = 'vg-pincode__ok';
          fillTemplate(line, this.dataset.resultText || 'Estimated delivery: [range]', '[range]', formatRange(win.from, win.to));
          this.result.appendChild(line);

          var meta = document.createElement('p');
          meta.className = 'vg-pincode__meta';
          var pinEl = document.createElement('span');
          pinEl.className = 'vg-pincode__pin';
          pinEl.textContent = fillWord(word(this, 'pincode-to', 'To [pincode]'), '[pincode]', pin);
          meta.appendChild(pinEl);
          if (this.dataset.codText) {
            var cod = document.createElement('span');
            cod.className = 'vg-pincode__cod';
            cod.textContent = this.dataset.codText;
            meta.appendChild(cod);
          }
          var change = document.createElement('button');
          change.type = 'button';
          change.className = 'vg-pincode__change';
          change.setAttribute('data-vg-pincode-change', '');
          change.textContent = word(this, 'pincode-change', 'Change');
          meta.appendChild(change);
          this.result.appendChild(meta);

          this.classList.add('is-checked');
          storageSet(PINCODE_KEY, pin);
        }
      }
    );
  }

  /* ---------------------------------------------------------------- WhatsApp order */
  if (!customElements.get('vg-whatsapp-order')) {
    customElements.define(
      'vg-whatsapp-order',
      class VgWhatsAppOrder extends HTMLElement {
        connectedCallback() {
          if (this.vgReady) return;
          this.vgReady = true;
          this.link = this.querySelector('[data-vg-whatsapp-link]');
          this.onVariant = this.onVariant.bind(this);
          this.onOption = this.onOption.bind(this);
          document.addEventListener('vg:variant', this.onVariant);
          document.addEventListener('vg:option', this.onOption);
        }

        disconnectedCallback() {
          this.vgReady = false;
          document.removeEventListener('vg:variant', this.onVariant);
          document.removeEventListener('vg:option', this.onOption);
        }

        onVariant(event) {
          var detail = event.detail;
          if (!detail || !detail.variant || String(detail.productId) !== String(this.dataset.productId)) return;
          this.update(detail.variant.title, detail.variant.id);
        }

        /* While Dawn fetches the new variant (or when the combination does not exist) the message
           follows the options the shopper picked, without a stale ?variant= id. */
        onOption(event) {
          var detail = event.detail;
          if (!detail || !detail.productInfo || !detail.productInfo.contains(this)) return;
          var selects = detail.productInfo.querySelector('variant-selects');
          if (!selects) return;
          var values = Array.prototype.map
            .call(selects.querySelectorAll('fieldset input:checked, select'), function (input) {
              return input.value;
            })
            .filter(Boolean);
          this.update(values.join(' / '), null);
        }

        update(variantTitle, variantId) {
          if (!this.link) return;
          var message = (this.dataset.intro || '') + ' ' + (this.dataset.title || '');
          if (this.dataset.defaultVariant !== 'true' && variantTitle) message += ' (' + variantTitle + ')';
          message += ' — ' + this.dataset.url + (variantId ? '?variant=' + variantId : '');
          this.link.href = 'https://wa.me/' + this.dataset.number + '?text=' + encodeURIComponent(message.trim());
        }
      }
    );
  }

  /* ---------------------------------------------------------------- Mobile gallery dots */
  if (!customElements.get('vg-gallery-dots')) {
    customElements.define(
      'vg-gallery-dots',
      class VgGalleryDots extends HTMLElement {
        connectedCallback() {
          if (this.vgReady) return;
          this.slider = this.closest('slider-component');
          this.list = this.slider ? this.slider.querySelector('[id^="Slider-"]') : null;
          if (!this.list) return;
          this.vgReady = true;
          this.count = -1;
          this.index = -1;
          this.onScroll = this.onScroll.bind(this);
          this.onTap = this.onTap.bind(this);
          this.list.addEventListener('scroll', this.onScroll, { passive: true });
          this.addEventListener('click', this.onTap);
          this.resizeObserver = new ResizeObserver(this.build.bind(this));
          this.resizeObserver.observe(this.list);
          this.mutationObserver = new MutationObserver(this.build.bind(this));
          this.mutationObserver.observe(this.list, { childList: true });
          this.build();
        }

        disconnectedCallback() {
          this.vgReady = false;
          if (this.list) this.list.removeEventListener('scroll', this.onScroll);
          this.removeEventListener('click', this.onTap);
          if (this.resizeObserver) this.resizeObserver.disconnect();
          if (this.mutationObserver) this.mutationObserver.disconnect();
          if (this.frame) window.cancelAnimationFrame(this.frame);
        }

        slides() {
          return Array.prototype.filter.call(this.list.querySelectorAll('[id^="Slide-"]'), function (slide) {
            return slide.clientWidth > 0;
          });
        }

        build() {
          var slides = this.slides();
          if (slides.length !== this.count) {
            this.count = slides.length;
            this.index = -1;
            this.textContent = '';
            this.hidden = this.count < 2;
            for (var i = 0; i < this.count; i++) {
              var dot = document.createElement('span');
              dot.className = 'vg-gallery-dots__dot';
              dot.dataset.index = String(i);
              this.appendChild(dot);
            }
          }
          this.update();
        }

        onScroll() {
          if (this.frame) return;
          this.frame = window.requestAnimationFrame(
            function () {
              this.frame = null;
              this.update();
            }.bind(this)
          );
        }

        update() {
          var slides = this.slides();
          if (slides.length < 2) return;
          var step = slides[1].offsetLeft - slides[0].offsetLeft || this.list.clientWidth || 1;
          var index = Math.min(Math.max(Math.round(this.list.scrollLeft / step), 0), slides.length - 1);
          if (index === this.index) return;
          this.index = index;
          Array.prototype.forEach.call(this.children, function (dot, i) {
            dot.classList.toggle('is-active', i === index);
          });
          /* Phones: the visible slide is the "active" media (fly-to-bag image, lightbox opener). */
          if (mobileMedia.matches) {
            slides.forEach(function (slide, i) {
              slide.classList.toggle('is-active', i === index);
            });
          }
        }

        onTap(event) {
          var dot = event.target.closest('[data-index]');
          if (!dot) return;
          var slide = this.slides()[parseInt(dot.dataset.index, 10)];
          if (slide) {
            this.list.scrollTo({
              left: slide.offsetLeft - this.list.firstElementChild.offsetLeft,
              behavior: reduceMotion.matches ? 'auto' : 'smooth',
            });
          }
        }
      }
    );
  }

  /* ---------------------------------------------------------------- Gallery: counter + vertical thumbnail rail */
  function initGallery(gallery) {
    if (gallery.vgGalleryReady || gallery.closest('quick-add-modal')) return;
    gallery.vgGalleryReady = true;

    var viewer = gallery.querySelector('[id^="Slider-Gallery"]');
    var counter = gallery.querySelector('[data-vg-gallery-counter]');
    if (viewer && counter) {
      var current = counter.querySelector('[data-vg-gallery-current]');
      var total = counter.querySelector('[data-vg-gallery-total]');
      var updateCounter = function () {
        var items = viewer.querySelectorAll('li[data-media-id]');
        var active = viewer.querySelector('li[data-media-id].is-active');
        var index = Math.max(Array.prototype.indexOf.call(items, active), 0);
        if (current) current.textContent = String(index + 1);
        if (total) total.textContent = String(items.length);
      };
      new MutationObserver(updateCounter).observe(viewer, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true,
        childList: true,
      });
      updateCounter();
    }

    var slider = gallery.querySelector('.thumbnail-slider');
    var list = slider ? slider.querySelector('.thumbnail-list') : null;
    if (!list) return;

    /* Keep the active thumbnail in view on the vertical rail */
    new MutationObserver(function (records) {
      if (!railMedia.matches) return;
      records.forEach(function (record) {
        if (record.attributeName !== 'aria-current' || !record.target.hasAttribute('aria-current')) return;
        var item = record.target.closest('.thumbnail-list__item');
        if (!item || list.scrollHeight <= list.clientHeight) return;
        var top = item.offsetTop - list.clientHeight / 2 + item.clientHeight / 2;
        list.scrollTo({ top: Math.max(top, 0), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      });
    }).observe(list, { attributes: true, subtree: true, attributeFilter: ['aria-current'] });

    var prev = slider.querySelector('[data-vg-rail-prev]');
    var next = slider.querySelector('[data-vg-rail-next]');
    if (!prev || !next) return;
    var frame = null;
    var updateRail = function () {
      frame = null;
      var scrollable = railMedia.matches && list.scrollHeight > list.clientHeight + 2;
      slider.classList.toggle('vg-rail--scroll', scrollable);
      if (!scrollable) return;
      prev.disabled = list.scrollTop <= 1;
      next.disabled = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
    };
    var schedule = function () {
      if (!frame) frame = window.requestAnimationFrame(updateRail);
    };
    var step = function (direction) {
      var item = list.querySelector('.thumbnail-list__item');
      var distance = item ? (item.offsetHeight + 12) * 3 : list.clientHeight * 0.7;
      list.scrollBy({ top: direction * distance, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    };
    prev.addEventListener('click', function () {
      step(-1);
    });
    next.addEventListener('click', function () {
      step(1);
    });
    list.addEventListener('scroll', schedule, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(list);
    if (typeof railMedia.addEventListener === 'function') railMedia.addEventListener('change', schedule);
    schedule();
  }

  /* ---------------------------------------------------------------- Accordions: smooth height */
  function animateDetails(details, content) {
    var opening = !details.open || details.classList.contains('is-closing');
    var start = details.open ? content.getBoundingClientRect().height : 0;
    if (details.vgAnimation) details.vgAnimation.cancel();
    details.classList.remove('is-closing');
    if (opening) {
      details.open = true;
    } else {
      details.classList.add('is-closing');
    }
    var end = opening ? content.scrollHeight : 0;
    content.style.overflow = 'hidden';
    var animation = content.animate(
      [
        { height: start + 'px', opacity: opening ? 0 : 1 },
        { height: end + 'px', opacity: opening ? 1 : 0 },
      ],
      { duration: Math.min(Math.max(Math.abs(end - start) * 0.6, 260), 520), easing: EASE }
    );
    details.vgAnimation = animation;
    animation.onfinish = function () {
      details.vgAnimation = null;
      content.style.overflow = '';
      if (!opening) {
        details.open = false;
        details.classList.remove('is-closing');
      }
    };
    animation.oncancel = function () {
      content.style.overflow = '';
    };
  }

  /* Delegated on document (bubble phase), so Dawn's own summary handler (aria-expanded) has run first. */
  document.addEventListener('click', function (event) {
    var summary = event.target.closest ? event.target.closest('summary') : null;
    if (!summary) return;
    var details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS' || !details.hasAttribute('data-vg-animate')) return;
    if (reduceMotion.matches || typeof details.animate !== 'function') return;
    var content = summary.nextElementSibling;
    if (!content) return;
    event.preventDefault();
    animateDetails(details, content);
  });

  /* ---------------------------------------------------------------- Media lightbox (VG.lightbox) */
  function lightboxItems(content) {
    return Array.prototype.filter.call(content.children, function (child) {
      return child.getClientRects().length > 0;
    });
  }

  function isStrip(content) {
    return mobileMedia.matches && content.scrollWidth > content.clientWidth + 1;
  }

  function createLightbox(modal, content) {
    var state = { scale: 1, x: 0, y: 0, img: null, pinch: null, pan: null, lastTap: null, frame: null };
    var currentEl = modal.querySelector('[data-vg-lb-current]');
    var totalEl = modal.querySelector('[data-vg-lb-total]');
    var counter = modal.querySelector('[data-vg-lb-counter]');

    function updateCounter() {
      state.frame = null;
      var items = lightboxItems(content);
      if (totalEl) totalEl.textContent = String(items.length);
      if (counter) counter.hidden = items.length < 2;
      if (!items.length) return;
      var box = content.getBoundingClientRect();
      var strip = isStrip(content);
      var centre = strip ? box.left + box.width / 2 : box.top + box.height / 2;
      var best = 0;
      var bestDistance = Infinity;
      items.forEach(function (item, index) {
        var rect = item.getBoundingClientRect();
        var mid = strip ? rect.left + rect.width / 2 : rect.top + rect.height / 2;
        var distance = Math.abs(mid - centre);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });
      if (currentEl) currentEl.textContent = String(best + 1);
      if (state.img && state.scale > 1 && items[best] !== state.img && !items[best].contains(state.img)) reset(false);
    }

    function apply(animate) {
      if (!state.img) return;
      state.img.classList.toggle('is-gesturing', !animate);
      state.img.style.transform =
        state.scale > 1 ? 'translate3d(' + state.x + 'px, ' + state.y + 'px, 0) scale(' + state.scale + ')' : '';
      state.img.classList.toggle('is-zoomed', state.scale > 1);
      content.classList.toggle('is-zoomed', state.scale > 1);
    }

    function reset(animate) {
      if (state.img) {
        state.scale = 1;
        state.x = 0;
        state.y = 0;
        apply(animate);
      }
      content.querySelectorAll('img.is-zoomed').forEach(function (img) {
        img.style.transform = '';
        img.classList.remove('is-zoomed', 'is-gesturing');
      });
      content.classList.remove('is-zoomed');
      state.scale = 1;
      state.x = 0;
      state.y = 0;
      state.img = null;
      state.pinch = null;
      state.pan = null;
    }

    /* Untransformed box of the image (transform-origin is 0 0) */
    function baseBox(img) {
      var rect = img.getBoundingClientRect();
      var scale = img === state.img ? state.scale : 1;
      var x = img === state.img ? state.x : 0;
      var y = img === state.img ? state.y : 0;
      return { left: rect.left - x, top: rect.top - y, width: rect.width / scale, height: rect.height / scale };
    }

    function clamp(box) {
      var minX = box.width - box.width * state.scale;
      var minY = box.height - box.height * state.scale;
      state.x = Math.min(0, Math.max(minX, state.x));
      state.y = Math.min(0, Math.max(minY, state.y));
    }

    function zoomAt(img, clientX, clientY, scale, animate) {
      var box = baseBox(img);
      var localX = (clientX - box.left - (img === state.img ? state.x : 0)) / (img === state.img ? state.scale : 1);
      var localY = (clientY - box.top - (img === state.img ? state.y : 0)) / (img === state.img ? state.scale : 1);
      if (state.img && state.img !== img) reset(false);
      state.img = img;
      state.scale = scale;
      state.x = clientX - box.left - localX * scale;
      state.y = clientY - box.top - localY * scale;
      clamp(box);
      apply(animate);
    }

    function distance(a, b) {
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    }

    function onTouchStart(event) {
      var img = event.target.closest ? event.target.closest('img') : null;
      if (!img || !content.contains(img) || img.closest('.deferred-media__poster')) return;
      if (event.touches.length === 2) {
        var a = event.touches[0];
        var b = event.touches[1];
        if (state.img !== img) reset(false);
        state.img = img;
        var box = baseBox(img);
        var midX = (a.clientX + b.clientX) / 2;
        var midY = (a.clientY + b.clientY) / 2;
        state.pinch = {
          distance: distance(a, b) || 1,
          scale: state.scale,
          localX: (midX - box.left - state.x) / state.scale,
          localY: (midY - box.top - state.y) / state.scale,
        };
        state.pan = null;
        state.lastTap = null;
        event.preventDefault();
      } else if (event.touches.length === 1) {
        var touch = event.touches[0];
        state.tapStart = { x: touch.clientX, y: touch.clientY, time: Date.now(), img: img };
        if (state.scale > 1 && state.img === img) {
          state.pan = { x: touch.clientX, y: touch.clientY, startX: state.x, startY: state.y };
        }
      }
    }

    function onTouchMove(event) {
      if (state.pinch && event.touches.length === 2 && state.img) {
        event.preventDefault();
        var a = event.touches[0];
        var b = event.touches[1];
        var box = baseBox(state.img);
        var midX = (a.clientX + b.clientX) / 2;
        var midY = (a.clientY + b.clientY) / 2;
        state.scale = Math.min(Math.max((state.pinch.scale * distance(a, b)) / state.pinch.distance, 1), 4);
        state.x = midX - box.left - state.pinch.localX * state.scale;
        state.y = midY - box.top - state.pinch.localY * state.scale;
        clamp(box);
        apply(false);
      } else if (state.pan && event.touches.length === 1 && state.img) {
        event.preventDefault();
        var touch = event.touches[0];
        state.x = state.pan.startX + touch.clientX - state.pan.x;
        state.y = state.pan.startY + touch.clientY - state.pan.y;
        clamp(baseBox(state.img));
        apply(false);
      }
    }

    function onTouchEnd(event) {
      if (state.pinch && event.touches.length < 2) {
        state.pinch = null;
        if (state.scale < 1.05) {
          reset(true);
        } else if (event.touches.length === 1) {
          var rest = event.touches[0];
          state.pan = { x: rest.clientX, y: rest.clientY, startX: state.x, startY: state.y };
        }
        if (state.img) state.img.classList.remove('is-gesturing');
        return;
      }
      if (event.touches.length === 0) {
        state.pan = null;
        if (state.img) state.img.classList.remove('is-gesturing');
      }

      /* Double tap: toggle 2.5x zoom at the tapped point */
      var start = state.tapStart;
      var touch = event.changedTouches && event.changedTouches[0];
      if (!start || !touch || event.touches.length) return;
      var moved = Math.hypot(touch.clientX - start.x, touch.clientY - start.y);
      if (moved > 12 || Date.now() - start.time > 300) {
        state.lastTap = null;
        return;
      }
      var last = state.lastTap;
      if (last && last.img === start.img && Date.now() - last.time < 320 && Math.hypot(touch.clientX - last.x, touch.clientY - last.y) < 36) {
        event.preventDefault();
        state.lastTap = null;
        if (state.scale > 1 && state.img === start.img) {
          reset(true);
        } else {
          zoomAt(start.img, touch.clientX, touch.clientY, 2.5, true);
        }
        return;
      }
      state.lastTap = { x: touch.clientX, y: touch.clientY, time: Date.now(), img: start.img };
    }

    content.addEventListener('touchstart', onTouchStart, { passive: false });
    content.addEventListener('touchmove', onTouchMove, { passive: false });
    content.addEventListener('touchend', onTouchEnd);
    content.addEventListener('touchcancel', function () {
      state.pinch = null;
      state.pan = null;
    });
    content.addEventListener(
      'scroll',
      function () {
        if (!state.frame) state.frame = window.requestAnimationFrame(updateCounter);
      },
      { passive: true }
    );

    return { reset: reset, updateCounter: updateCounter };
  }

  window.VG.lightbox = {
    open: function (modal, activeMedia) {
      var content = modal.querySelector('.product-media-modal__content');
      if (!content) return;
      if (!modal.vgLightbox) modal.vgLightbox = createLightbox(modal, content);
      modal.vgLightbox.reset(false);
      if (activeMedia && isStrip(content)) {
        content.scrollLeft += activeMedia.getBoundingClientRect().left - content.getBoundingClientRect().left;
      }
      modal.vgLightbox.updateCounter();
      if (mobileMedia.matches && !window.VG.lightboxHintShown) {
        window.VG.lightboxHintShown = true;
        modal.classList.add('vg-lightbox--hint');
        window.setTimeout(function () {
          modal.classList.remove('vg-lightbox--hint');
        }, 2800);
      }
    },
    close: function (modal) {
      if (modal.vgLightbox) modal.vgLightbox.reset(false);
      modal.classList.remove('vg-lightbox--hint');
    },
  };

  /* ---------------------------------------------------------------- Share row */
  function initShare(root) {
    (root || document).querySelectorAll('[data-vg-share]').forEach(function (share) {
      if (share.vgShareReady) return;
      share.vgShareReady = true;
      /* With the explicit "Copy link" button, Dawn's copy fallback (desktop) is redundant; the native share
         button (Web Share API) stays as "More". */
      if (!navigator.share && share.classList.contains('vg-share--has-copy')) {
        var native = share.querySelector('share-button');
        if (native) native.hidden = true;
      }
    });
  }

  /* ---------------------------------------------------------------- Size guide drawer */
  if (!customElements.get('vg-size-guide')) {
    customElements.define(
      'vg-size-guide',
      class VgSizeGuide extends HTMLElement {
        connectedCallback() {
          if (this.vgReady) return;
          this.dialog = this.querySelector('dialog');
          if (!this.dialog) return;
          this.vgReady = true;
          var self = this;
          this.querySelectorAll('[data-vg-unit]').forEach(function (button) {
            button.addEventListener('click', function () {
              self.setUnit(button.dataset.vgUnit, true);
            });
          });
          this.querySelectorAll('[data-vg-dialog-close]').forEach(function (button) {
            button.addEventListener('click', function () {
              self.close();
            });
          });
          this.dialog.addEventListener('click', function (event) {
            if (event.target === self.dialog) self.close();
          });
          this.dialog.addEventListener('cancel', function (event) {
            event.preventDefault();
            self.close();
          });
          var saved = storageGet('vg:size-unit');
          this.setUnit(saved === 'cm' || saved === 'in' ? saved : this.dataset.unit || 'in', false);
        }

        disconnectedCallback() {
          if (this.dialog && this.dialog.open) document.documentElement.classList.remove('vg-dialog-open');
        }

        setUnit(unit, remember) {
          this.unit = unit === 'cm' ? 'cm' : 'in';
          var self = this;
          this.querySelectorAll('[data-vg-unit]').forEach(function (button) {
            button.setAttribute('aria-pressed', String(button.dataset.vgUnit === self.unit));
          });
          this.querySelectorAll('td[data-in]').forEach(function (cell) {
            cell.textContent = self.unit === 'cm' ? cell.dataset.cm : cell.dataset.in;
          });
          var label = this.querySelector('[data-vg-unit-label]');
          if (label) label.textContent = this.unit === 'cm' ? 'centimetres' : 'inches';
          if (remember) storageSet('vg:size-unit', this.unit);
        }

        open(opener) {
          if (!this.dialog || this.dialog.open) return;
          this.opener = opener || document.activeElement;
          if (typeof this.dialog.showModal === 'function') {
            this.dialog.showModal();
          } else {
            this.dialog.setAttribute('open', '');
          }
          document.documentElement.classList.add('vg-dialog-open');
          var dialog = this.dialog;
          var panel = dialog.querySelector('.vg-size-guide__panel');
          /* showModal() autofocuses inside the panel while it is still translated off-screen, which scrolls the
             dialog box and leaves the sheet's top cut off on phones. Undo that scroll and focus without scrolling. */
          dialog.scrollTop = 0;
          if (panel) panel.scrollTop = 0;
          var close = this.dialog.querySelector('[data-vg-dialog-close]');
          if (close) close.focus({ preventScroll: true });
          window.requestAnimationFrame(function () {
            dialog.scrollTop = 0;
            dialog.classList.add('is-open');
          });
        }

        close() {
          if (!this.dialog || !this.dialog.open) return;
          var dialog = this.dialog;
          var opener = this.opener;
          dialog.classList.remove('is-open');
          var finish = function () {
            if (typeof dialog.close === 'function') {
              dialog.close();
            } else {
              dialog.removeAttribute('open');
            }
            document.documentElement.classList.remove('vg-dialog-open');
            if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
          };
          if (reduceMotion.matches) {
            finish();
          } else {
            window.setTimeout(finish, 460); /* matches the .vg-size-guide__panel transition */
          }
        }
      }
    );
  }

  function sizeGuideFor(id) {
    var dialog = id ? document.getElementById(id) : null;
    return dialog ? dialog.closest('vg-size-guide') : null;
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest ? event.target.closest('[data-vg-size-guide-open]') : null;
    if (!opener) return;
    var guide = sizeGuideFor(opener.dataset.vgSizeGuideOpen);
    if (!guide) return;
    event.preventDefault();
    guide.open(opener);
  });

  /* Theme editor: open the drawer while its block is selected */
  document.addEventListener('shopify:block:select', function (event) {
    if (!event.target.classList || !event.target.classList.contains('vg-size-guide-block')) return;
    var section = event.target.closest('.shopify-section');
    var guide = section ? section.querySelector('vg-size-guide') : null;
    if (guide) guide.open(null);
  });
  document.addEventListener('shopify:block:deselect', function (event) {
    if (!event.target.classList || !event.target.classList.contains('vg-size-guide-block')) return;
    var section = event.target.closest('.shopify-section');
    var guide = section ? section.querySelector('vg-size-guide') : null;
    if (guide) guide.close();
  });

  /* ---------------------------------------------------------------- Swatchfy bridge
     The Swatchfy app embed hides Dawn's <variant-selects> and draws its own rows (it still drives the hidden inputs,
     so Dawn's variant change, our sticky size row and the notify panel keep working). Its label row has a public
     slot for "a size chart link": our size guide button would otherwise be hidden with the theme picker. Low-stock
     sizes get the same dot as the theme picker. Re-applied on every swatchfyOptionRendered (rows are rebuilt). */
  var swatchfyFrame = null;

  function bridgeSwatchfy() {
    swatchfyFrame = null;
    document.querySelectorAll('product-info').forEach(function (info) {
      var selects = info.querySelector('variant-selects');
      if (!selects) return;
      info.querySelectorAll('.swatchfy-swatches[data-swatchfy-option]').forEach(function (row) {
        var name = row.getAttribute('data-swatchfy-option') || '';
        var slot = row.querySelector('[data-swatchfy-slot]');
        if (slot && /size/i.test(name) && !slot.querySelector('.vg-size-guide-link')) {
          var guide = selects.querySelector('.vg-size-guide-link');
          if (guide) {
            var link = guide.cloneNode(true);
            link.classList.add('vg-size-guide-link--slot');
            slot.appendChild(link);
          }
        }
        var inputs = Array.prototype.filter.call(selects.querySelectorAll('input[type="radio"]'), function (input) {
          return input.dataset.optionName === name;
        });
        if (!inputs.length) return;
        row.querySelectorAll('[data-swatchfy-value]').forEach(function (button) {
          var value = button.getAttribute('data-swatchfy-value');
          var input = inputs.find(function (el) {
            return el.value === value;
          });
          var label = input && input.id ? selects.querySelector('label[for="' + cssEscape(input.id) + '"]') : null;
          var low = Boolean(label && label.classList.contains('vg-value--low'));
          button.classList.toggle('vg-sfy-low', low);
          if (low && label.title) {
            button.setAttribute('data-vg-low', label.title);
          } else {
            button.removeAttribute('data-vg-low');
          }
        });
      });
    });
  }

  function scheduleSwatchfy() {
    if (!swatchfyFrame) swatchfyFrame = window.requestAnimationFrame(bridgeSwatchfy);
  }

  document.addEventListener('swatchfyOptionRendered', scheduleSwatchfy);
  document.addEventListener('vg:variant', scheduleSwatchfy);

  /* ---------------------------------------------------------------- Related products: load early
     Dawn loads <product-recommendations> 400px before it scrolls into view, so a quick scroll on a phone lands
     on the section while it renders and pushes the footer down (CLS ~0.2). Load it on idle instead, while it is
     still far below the viewport; Dawn's observer is released so there is a single request. */
  function prefetchRecommendations() {
    document.querySelectorAll('product-recommendations.related-products').forEach(function (el) {
      if (el.vgPrefetched || el.classList.contains('product-recommendations--loaded')) return;
      if (typeof el.loadRecommendations !== 'function' || !el.dataset.productId) return;
      var top = el.getBoundingClientRect().top;
      if (top < window.innerHeight * 1.25) return; /* close by: Dawn's own observer handles it */
      el.vgPrefetched = true;
      if (el.observer && typeof el.observer.disconnect === 'function') el.observer.disconnect();
      el.loadRecommendations(el.dataset.productId);
    });
  }

  function onIdle(callback) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 3000 });
    } else {
      window.setTimeout(callback, 1200);
    }
  }

  if (document.readyState === 'complete') {
    onIdle(prefetchRecommendations);
  } else {
    window.addEventListener(
      'load',
      function () {
        onIdle(prefetchRecommendations);
      },
      { once: true }
    );
  }

  /* ---------------------------------------------------------------- Init (page, theme editor reloads, quick add) */
  function init(root) {
    var scope = root || document;
    scope.querySelectorAll('.vg-pdp media-gallery').forEach(initGallery);
    initShare(scope);
    scheduleSwatchfy();
  }

  init(document);
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('product-info:loaded', function (event) {
    if (event.target && event.target.querySelectorAll) init(event.target);
  });
})();
